import { CustomAttribute, INode, resolve } from 'aurelia';
import { IdleAnimation, IdleAnimationStage } from './idle-animation';
import { ToyParadeAnimation } from './toy-parade-animation';
import { ShootingStarAnimation } from './shooting-star-animation';
import { SpaceEncounterAnimation } from './space-encounter-animation';
import { MarioLevelAnimation } from './mario-level-animation';

/**
 * Idle-animation director for the footer strip.
 *
 * Owns all the (relatively) expensive gating so decorative animations stay
 * cheap: it only fires after a quiet spell, only while the footer is on screen
 * and the tab is visible, and respects prefers-reduced-motion. Each time it
 * fires it picks ONE animation at random from {@link REGISTRY} and plays it,
 * then re-arms for the next quiet spell — so runtime cost is always a single
 * animation, never the sum of the pool.
 *
 * Design mirrors the original toy-parade attribute: a self-contained decorative
 * layer that owns its own DOM (the strip), gates on visibility/idle, and fully
 * cleans up on detach. The individual choreography lives in the registered
 * {@link IdleAnimation} implementations; this class never renders frames itself.
 *
 * The attribute is named `idle-animation` and is applied to the footer
 * (`<footer class="app-footer" idle-animation>`).
 */
export class IdleAnimationDirector {
  // --- tuning knobs --------------------------------------------------------
  private static readonly IDLE_DELAY = 10000; // ms of inactivity before playing
  // Below this viewport width the full-page ("page" stage) animations are
  // skipped. On phones the calendar is full-width and those animations fly
  // actors across the whole screen, which both reads poorly and can perturb
  // page layout under the fixed header. The lightweight footer-strip animations
  // still play there.
  private static readonly PAGE_STAGE_MIN_WIDTH = 700; // px
  // -------------------------------------------------------------------------

  // The pool the director randomly rotates through. Add CSS-keyframe driven,
  // self-tearing-down animations here; avoid continuous rAF/canvas effects.
  private static readonly REGISTRY: ReadonlyArray<IdleAnimation> = [
    new ToyParadeAnimation(),
    new ShootingStarAnimation(),
    new SpaceEncounterAnimation(),
    new MarioLevelAnimation()
  ];

  private readonly host = resolve(INode) as HTMLElement;

  private strip: HTMLElement | null = null;
  private observer: IntersectionObserver | null = null;
  private idleTimer = 0;
  private isVisible = false;
  private playing = false;
  private enabled = false;
  private runController: AbortController | null = null;
  private lastAnimation: IdleAnimation | null = null;

  private readonly onActivity = (): void => this.scheduleIdle();
  private readonly onVisibility = (): void => {
    if (document.hidden) {
      this.clearIdle();
    } else {
      this.scheduleIdle();
    }
  };

  attached(): void {
    // Respect reduced-motion users: keep the app calm, add no movement.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // A thin strip anchored to the footer's top border line. Animations spawn
    // their actors here and are clipped as they enter/exit.
    const strip = document.createElement('div');
    strip.className = 'toy-parade-strip';
    strip.setAttribute('aria-hidden', 'true');
    this.host.appendChild(strip);
    this.strip = strip;
    this.enabled = true;

    // Only bother while the footer is actually in view.
    this.observer = new IntersectionObserver(
      (entries) => {
        this.isVisible = entries.some((e) => e.isIntersecting);
        if (this.isVisible && !document.hidden) {
          this.scheduleIdle();
        } else {
          this.clearIdle();
        }
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this.host);

    // Any interaction resets the idle countdown, so an animation only appears
    // once the user has been still for a while.
    window.addEventListener('mousemove', this.onActivity, { passive: true });
    window.addEventListener('keydown', this.onActivity, { passive: true });
    window.addEventListener('scroll', this.onActivity, { passive: true });
    window.addEventListener('touchstart', this.onActivity, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  detaching(): void {
    this.clearIdle();
    // Abort any in-flight animation so it stops and cleans up its nodes.
    this.runController?.abort();
    this.runController = null;
    this.observer?.disconnect();
    this.observer = null;
    window.removeEventListener('mousemove', this.onActivity);
    window.removeEventListener('keydown', this.onActivity);
    window.removeEventListener('scroll', this.onActivity);
    window.removeEventListener('touchstart', this.onActivity);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.strip?.remove();
    this.strip = null;
    this.enabled = false;
    this.playing = false;
  }

  private scheduleIdle(): void {
    if (!this.enabled || this.playing || !this.isVisible || document.hidden) {
      return;
    }
    this.clearIdle();
    this.idleTimer = window.setTimeout(() => void this.play(), IdleAnimationDirector.IDLE_DELAY);
  }

  private clearIdle(): void {
    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = 0;
    }
  }

  // Runs one pass of a randomly chosen animation, then re-arms the idle trigger
  // so a (possibly different) animation can play after the next quiet spell.
  private async play(): Promise<void> {
    const registry = IdleAnimationDirector.REGISTRY;
    if (!this.strip || this.playing || registry.length === 0) {
      return;
    }
    this.playing = true;

    const controller = new AbortController();
    this.runController = controller;

    // On narrow (mobile) viewports the wider stages ('page', 'fullpage' and
    // 'footer-below-calendar') are skipped — they measure the calendar and fly
    // actors across it, which reads poorly and can disturb the layout under the
    // fixed header when the calendar is full-width. Restrict the pool to the
    // lightweight footer-strip effects there; if nothing is eligible, bail out
    // gracefully.
    const allowPageStage =
      window.innerWidth >= IdleAnimationDirector.PAGE_STAGE_MIN_WIDTH;
    const pool = allowPageStage
      ? registry
      : registry.filter((a) => a.stage === 'footer');
    if (pool.length === 0) {
      this.playing = false;
      this.runController = null;
      return;
    }

    // Avoid replaying the animation that ran last time. When more than one
    // option is eligible, draw from the pool minus the previous pick so the
    // same effect never appears twice in a row; with a single eligible option
    // we have no choice but to repeat it.
    const candidates =
      pool.length > 1 && this.lastAnimation
        ? pool.filter((a) => a !== this.lastAnimation)
        : pool;
    const choicePool = candidates.length > 0 ? candidates : pool;

    const animation = choicePool[Math.floor(Math.random() * choicePool.length)];
    this.lastAnimation = animation;
    // Resolve the stage this animation wants. The footer strip is persistent;
    // a page stage is created on demand for this pass and torn down after.
    const { stage, dispose } = this.resolveStage(animation.stage);
    try {
      await animation.play(stage, controller.signal);
    } finally {
      dispose();
      if (this.runController === controller) {
        this.runController = null;
      }
      this.playing = false;
      // Only re-arm if we weren't torn down mid-pass.
      if (!controller.signal.aborted) {
        this.scheduleIdle();
      }
    }
  }

  // Returns the mount element for the requested stage plus a disposer. The
  // footer strip is reused (never disposed here); the page-level layers are
  // created lazily and removed once the pass finishes so they never overlay
  // content while idle.
  //
  // For the `page` stage we anchor the layer to the calendar's box (so actors
  // play over the calendar rather than the whole viewport) and mount it inside
  // the scroll container just above the background but beneath the UI panels. If
  // the calendar can't be located we fall back to a fixed, viewport-sized layer.
  //
  // For the `fullpage` stage we always mount a dedicated fixed, full-viewport
  // layer that spans the entire page regardless of the calendar's box.
  private resolveStage(stage: IdleAnimationStage): { stage: HTMLElement; dispose: () => void } {
    if (stage === 'fullpage') {
      // A dedicated stage that always takes the full page: a fixed layer sized
      // to the whole viewport, sitting just above the Cosmos backdrop but behind
      // all app content and never intercepting pointer events.
      const page = document.createElement('div');
      page.className = 'idle-stage idle-stage--fullpage';
      page.setAttribute('aria-hidden', 'true');
      document.body.appendChild(page);
      return { stage: page, dispose: () => page.remove() };
    }

    if (stage === 'footer-below-calendar') {
      // A strip on the footer's top line (like the persistent footer strip) but
      // horizontally clamped to the calendar's left/right boundaries so the scene
      // reads as a "level" contained under the calendar. Mounted on the footer
      // host so its CSS `bottom: 100%` anchors it to the footer's top border.
      const band = document.createElement('div');
      band.className = 'idle-stage--footer-below-calendar';
      band.setAttribute('aria-hidden', 'true');

      // Clamp horizontally to the calendar panel's box. Query .panel-center
      // directly (the <calendar-grid> element may be containerless). Coordinates
      // are relative to the footer host, so translate the calendar's viewport
      // rect into the host's local space.
      const calendar =
        document.querySelector<HTMLElement>('.panel-center') ??
        document.querySelector<HTMLElement>('calendar-grid');
      const hostRect = this.host.getBoundingClientRect();
      if (calendar) {
        const calRect = calendar.getBoundingClientRect();
        band.style.left = `${calRect.left - hostRect.left}px`;
        band.style.width = `${calRect.width}px`;
      } else {
        // Fallback: span the whole footer width.
        band.style.left = '0';
        band.style.right = '0';
      }

      this.host.appendChild(band);
      return { stage: band, dispose: () => band.remove() };
    }

    if (stage === 'page') {
      const page = document.createElement('div');
      page.className = 'idle-stage';
      page.setAttribute('aria-hidden', 'true');

      // Measure the calendar's laid-out panel. Query .panel-center directly —
      // the <calendar-grid> custom element may be containerless (not present in
      // the DOM) or display:inline, so scoping under it (or measuring it) fails.
      const calendar =
        document.querySelector<HTMLElement>('.panel-center') ??
        document.querySelector<HTMLElement>('calendar-grid');
      if (calendar) {
        // Translate the calendar panel's viewport rect into ABSOLUTE PAGE
        // coordinates (viewport position + current page scroll). Mounting on
        // <body> with position:absolute means these page coordinates place the
        // band exactly under the calendar regardless of intermediate scroll
        // containers, offset parents, or transforms.
        const bandHeight = 120;
        const calRect = calendar.getBoundingClientRect();
        const pageLeft = calRect.left + window.scrollX;
        const pageTop = calRect.bottom + window.scrollY;
        page.classList.add('idle-stage--calendar');
        page.style.setProperty('position', 'absolute', 'important');
        page.style.setProperty('inset', 'auto', 'important');
        page.style.setProperty('left', `${pageLeft}px`, 'important');
        page.style.setProperty('top', `${pageTop}px`, 'important');
        page.style.setProperty('width', `${calRect.width}px`, 'important');
        page.style.setProperty('height', `${bandHeight}px`, 'important');
        document.body.appendChild(page);
        return { stage: page, dispose: () => page.remove() };
      }

      // Fallback: viewport-sized fixed layer appended to the footer host.
      page.classList.add('idle-stage--page');
      this.host.appendChild(page);
      return { stage: page, dispose: () => page.remove() };
    }
    // Default: the persistent footer strip.
    return { stage: this.strip!, dispose: () => {} };
  }
}

export const IdleAnimationDirectorAttribute = CustomAttribute.define({ name: 'idle-animation' }, IdleAnimationDirector);
